import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Servicio que gestiona el estado de visibilidad de múltiples spinners identificados por un ID.
 * 
 * Permite mostrar u ocultar spinners de forma individual usando un identificador único.
 */
@Injectable({
  providedIn: 'root'
})
export class SpinnerService {
  /**
   * Diccionario que asocia un ID de spinner con su estado observable.
   * Cada spinner tiene su propio `BehaviorSubject<boolean>`.
   */
  private spinnerStates: { [key: string]: BehaviorSubject<boolean> } = {};

  /**
   * Muestra el spinner con el ID especificado.
   *
   * @param id Identificador único del spinner que se quiere mostrar.
   */
  show(id: string): void {
    this.getOrCreateSpinner(id).next(true);
  }

  /**
   * Oculta el spinner con el ID especificado.
   *
   * @param id Identificador único del spinner que se quiere ocultar.
   */
  hide(id: string): void {
    this.getOrCreateSpinner(id).next(false);
  }

  /**
   * Obtiene el observable del estado del spinner con el ID dado.
   * Puede usarse para mostrar u ocultar el spinner en el template.
   *
   * @param id Identificador del spinner.
   * @returns Observable que emite `true` o `false` según la visibilidad del spinner.
   */
  getSpinnerState(id: string): Observable<boolean> {
    return this.getOrCreateSpinner(id).asObservable();
  }

  /**
   * Devuelve el `BehaviorSubject` correspondiente al ID proporcionado.
   * Si no existe, lo crea con valor inicial `false` (spinner oculto).
   *
   * @param id Identificador del spinner.
   * @returns `BehaviorSubject` que mantiene el estado actual del spinner.
   */
  private getOrCreateSpinner(id: string): BehaviorSubject<boolean> {
    if (!this.spinnerStates[id]) {
      this.spinnerStates[id] = new BehaviorSubject<boolean>(false);
    }
    return this.spinnerStates[id];
  }
}
